// frontend/src/pages/Reports.jsx (v2.4)
import React, { useState, useEffect } from 'react';
import '../styles/Reports.css';

function Reports({ user, token }) {
  const [reportType, setReportType] = useState('mooring-status');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const generateReport = async (type) => {
    setLoading(true);
    setError('');
    setReportData(null);

    try {
      const response = await fetch(
        `${type === 'audit-log' ? '/api/audit-log' : '/api/reports/' + type}?format=json`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Failed to generate report');
        setLoading(false);
        return;
      }

      const data = await response.json();
      setReportData(data);
    } catch (err) {
      console.error('Report error:', err);
      setError('Failed to generate report');
    }
    setLoading(false);
  };

  // CSV built client-side from already-loaded data — no auth needed
  const downloadCSV = () => {
    if (!reportData || reportData.length === 0) return;

    let headers = [];
    let rows = [];

    if (reportType === 'mooring-status') {
      headers = ['Harbor', 'Mooring', 'Member', 'Boat', 'Type', 'Registration', 'Departure'];
      rows = reportData.map(m => [
        m.harbor_name, m.mooring_number, m.member_name || '',
        m.boat_name, m.boat_type, m.boat_registration || '', m.departure_date
      ]);
    } else if (reportType === 'poaching') {
      headers = ['Boat Name', 'Registration', 'Hailing Port', 'Mooring', 'Harbor', 'Date Reported', 'Photos'];
      rows = reportData.map(p => [
        p.boat_name, p.boat_registration, p.hailing_port,
        p.mooring_id, p.harbor_name, p.reported_date, p.photo_count
      ]);
    } else if (reportType === 'club-burgee') {
      headers = ['Member', 'Boat Used', 'Own Boat', 'Mooring', 'Harbor', 'Check-in Date'];
      rows = reportData.map(c => [
        c.member_name, c.borrowed_boat_name, c.own_boat_name || '',
        c.mooring_id, c.harbor_name, c.check_in_date
      ]);
    } else if (reportType === 'usage-history') {
      headers = ['Member', 'Harbor', 'Mooring', 'Boat Type', 'Boat Name', 'Boat Class', 'Check-in Date', 'Departure Date', 'Checked Out', 'Status', 'Days Used', 'Photos'];
      rows = reportData.map(h => [
        h.member_name, h.harbor_name, h.mooring_id,
        h.boat_type, h.boat_name, h.member_type_boat || '', h.check_in_date, h.departure_date,
        h.checked_out_at ? h.checked_out_at.split('T')[0] : '',
        h.status, h.days_used, h.photo_count
      ]);
    } else if (reportType === 'mooring-days') {
      headers = ['Member', 'Harbor', 'Mooring', 'Boat', 'Boat Class', 'Type', 'Check-in Date', 'Departure Date', 'Checked Out Date', 'Status', 'Mooring Nights', 'Mooring Days'];
      rows = reportData.map(h => [
        h.member_name, h.harbor_name, h.mooring_id,
        h.boat_name, h.member_type_boat || '', h.boat_type, h.check_in_date,
        h.departure_date, h.checked_out_date || '', h.status, h.mooring_nights, h.mooring_days
      ]);
    } else if (reportType === 'audit-log') {
      headers = ['Date', 'Action', 'Table', 'Harbor', 'Mooring', 'Boat', 'Description', 'Changed By'];
      rows = reportData.map(a => [
        a.created_at ? a.created_at.split('T')[0] : '',
        a.action,
        a.table_name,
        a.harbor_name || '',
        a.mooring_number || '',
        a.boat_name || '',
        a.action_description || '',
        a.changed_by_name || ''
      ]);
    }

    const escape = (val) => {
      const s = val == null ? '' : String(val);
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? '"' + s.replace(/"/g, '""') + '"'
        : s;
    };

    const csv = [
      headers.join(','),
      ...rows.map(row => row.map(escape).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = window.URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = reportType + '-report.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  // Print — opens a clean formatted window
  const printReport = () => {
    if (!reportData || reportData.length === 0) return;

    let tableHTML = '';

    if (reportType === 'mooring-status') {
      const grouped = reportData.reduce((acc, row) => {
        if (!acc[row.harbor_name]) acc[row.harbor_name] = [];
        acc[row.harbor_name].push(row);
        return acc;
      }, {});
      tableHTML = Object.entries(grouped).map(function(entry) {
        var harbor = entry[0];
        var rows = entry[1];
        var rowsHTML = rows.map(function(m) {
          var boatType = (m.boat_type || '').replace(/_/g, ' ');
          return '<tr>'
            + '<td>' + m.mooring_number + '</td>'
            + '<td>' + (m.member_name || '') + '</td>'
            + '<td>' + m.boat_name + '</td>'
            + '<td>' + boatType + '</td>'
            + '<td>' + (m.boat_registration || '') + '</td>'
            + '<td>' + m.departure_date + '</td>'
            + '</tr>';
        }).join('');
        return '<h3>' + harbor + '</h3>'
          + '<table>'
          + '<thead><tr><th>Mooring</th><th>Member</th><th>Boat</th><th>Type</th>'
          + '<th>Registration</th><th>Departure</th></tr></thead>'
          + '<tbody>' + rowsHTML + '</tbody>'
          + '</table>';
      }).join('');

    } else if (reportType === 'usage-history') {
      const grouped = reportData.reduce((acc, row) => {
        if (!acc[row.harbor_name]) acc[row.harbor_name] = [];
        acc[row.harbor_name].push(row);
        return acc;
      }, {});

      tableHTML = Object.entries(grouped).map(function(entry) {
        var harbor = entry[0];
        var rows = entry[1];
        var rowsHTML = rows.map(function(r) {
          var boatType = (r.boat_type || '').replace(/_/g, ' ');
          return '<tr>'
            + '<td>' + (r.member_name || '') + '</td>'
            + '<td>' + r.mooring_id + '</td>'
            + '<td>' + r.boat_name + '</td>'
            + '<td>' + (r.member_type_boat || '') + '</td>'
            + '<td>' + boatType + '</td>'
            + '<td>' + r.check_in_date + '</td>'
            + '<td>' + r.departure_date + '</td>'
            + '<td>' + (r.status || '') + '</td>'
            + '<td>' + (r.days_used || '') + '</td>'
            + '</tr>';
        }).join('');
        return '<h3>' + harbor + '</h3>'
          + '<table>'
          + '<thead><tr><th>Member</th><th>Mooring</th><th>Boat</th><th>Boat Class</th><th>Type</th>'
          + '<th>Check-in</th><th>Departure</th><th>Status</th><th>Days</th></tr></thead>'
          + '<tbody>' + rowsHTML + '</tbody>'
          + '</table>';
      }).join('');

    } else if (reportType === 'mooring-days') {
      const grouped = reportData.reduce((acc, row) => {
        if (!acc[row.harbor_name]) acc[row.harbor_name] = [];
        acc[row.harbor_name].push(row);
        return acc;
      }, {});

      tableHTML = Object.entries(grouped).map(function(entry) {
        var harbor = entry[0];
        var rows = entry[1];
        var rowsHTML = rows.map(function(r) {
          var boatType = (r.boat_type || '').replace(/_/g, ' ');
          return '<tr>'
            + '<td>' + (r.member_name || '') + '</td>'
            + '<td>' + r.mooring_id + '</td>'
            + '<td>' + r.boat_name + '</td>'
            + '<td>' + (r.member_type_boat || '') + '</td>'
            + '<td>' + boatType + '</td>'
            + '<td>' + r.check_in_date + '</td>'
            + '<td>' + r.departure_date + '</td>'
            + '<td>' + (r.checked_out_date || '') + '</td>'
            + '<td>' + (r.status || '') + '</td>'
            + '<td>' + (r.mooring_nights != null ? r.mooring_nights : '') + '</td>'
            + '<td>' + (r.mooring_days != null ? r.mooring_days : '') + '</td>'
            + '</tr>';
        }).join('');
        return '<h3>' + harbor + '</h3>'
          + '<table>'
          + '<thead><tr><th>Member</th><th>Mooring</th><th>Boat</th><th>Boat Class</th><th>Type</th>'
          + '<th>Check-in</th><th>Departure</th><th>Checked Out</th><th>Status</th><th>Mooring Nights</th><th>Mooring Days</th></tr></thead>'
          + '<tbody>' + rowsHTML + '</tbody>'
          + '</table>';
      }).join('');

    } else if (reportType === 'poaching') {
      var poachRows = reportData.map(function(p) {
        return '<tr>'
          + '<td>' + p.boat_name + '</td>'
          + '<td>' + p.boat_registration + '</td>'
          + '<td>' + (p.hailing_port || '') + '</td>'
          + '<td>' + p.mooring_id + '</td>'
          + '<td>' + p.harbor_name + '</td>'
          + '<td>' + p.reported_date + '</td>'
          + '<td>' + p.photo_count + '</td>'
          + '</tr>';
      }).join('');
      tableHTML = '<table>'
        + '<thead><tr><th>Boat Name</th><th>Registration</th><th>Hailing Port</th>'
        + '<th>Mooring</th><th>Harbor</th><th>Date Reported</th><th>Photos</th></tr></thead>'
        + '<tbody>' + poachRows + '</tbody></table>';

    } else if (reportType === 'audit-log') {
      var auditRows = reportData.map(function(a) {
        return '<tr>'
          + '<td>' + (a.created_at ? a.created_at.split('T')[0] : '') + '</td>'
          + '<td>' + (a.action || '') + '</td>'
          + '<td>' + (a.harbor_name || '') + '</td>'
          + '<td>' + (a.mooring_number || '') + '</td>'
          + '<td>' + (a.boat_name || '') + '</td>'
          + '<td>' + (a.action_description || '') + '</td>'
          + '<td>' + (a.changed_by_name || '') + '</td>'
          + '</tr>';
      }).join('');
      tableHTML = '<table>'
        + '<thead><tr><th>Date</th><th>Action</th><th>Harbor</th><th>Mooring</th><th>Boat</th><th>Description</th><th>Changed By</th></tr></thead>'
        + '<tbody>' + auditRows + '</tbody></table>';

    } else if (reportType === 'club-burgee') {
      var burgeeRows = reportData.map(function(c) {
        return '<tr>'
          + '<td>' + c.member_name + '</td>'
          + '<td>' + c.borrowed_boat_name + '</td>'
          + '<td>' + (c.own_boat_name || '') + '</td>'
          + '<td>' + c.mooring_id + '</td>'
          + '<td>' + c.harbor_name + '</td>'
          + '<td>' + c.check_in_date + '</td>'
          + '</tr>';
      }).join('');
      tableHTML = '<table>'
        + '<thead><tr><th>Member</th><th>Boat Used</th><th>Own Boat</th>'
        + '<th>Mooring</th><th>Harbor</th><th>Check-in Date</th></tr></thead>'
        + '<tbody>' + burgeeRows + '</tbody></table>';
    }

    var titles = {
      'mooring-status': 'Current Mooring Status',
      'poaching':     'Poaching / Unknown Boats Report',
      'club-burgee':  'Club Burgee Activity Report',
      'usage-history':'Mooring Usage History Report',
      'mooring-days': 'Mooring Nights Report',
      'audit-log': 'Audit Log Report'
    };

    var win = window.open('', '_blank');
    win.document.write(
      '<html><head>'
      + '<title>' + (titles[reportType] || 'Report') + '</title>'
      + '<style>'
      + 'body { font-family: Arial, sans-serif; font-size: 12px; margin: 20px; }'
      + 'h2 { color: #003377; border-bottom: 2px solid #003377; padding-bottom: 6px; }'
      + 'h3 { color: #0066cc; margin-top: 20px; }'
      + 'table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }'
      + 'th { background: #003377; color: white; padding: 6px 8px; text-align: left; font-size: 11px; }'
      + 'td { padding: 5px 8px; border-bottom: 1px solid #ddd; }'
      + 'tr:nth-child(even) { background: #f5f8ff; }'
      + '.print-date { color: #666; font-size: 11px; margin-bottom: 16px; }'
      + '</style>'
      + '</head><body>'
      + '<h2>' + (titles[reportType] || 'Report') + '</h2>'
      + '<p class="print-date">Printed: ' + new Date().toLocaleString() + '</p>'
      + tableHTML
      + '</body></html>'
    );
    win.document.close();
    win.focus();
    win.print();
  };

  useEffect(() => {
    generateReport(reportType);
  }, [reportType]);

  const canExport = reportData && reportData.length > 0;

  const boatTypeLabel = (type) => {
    const labels = {
      member_boat_1: 'My Boat 1',
      member_boat_2: 'My Boat 2',
      member_on_other_boat: 'Club Burgee',
      member_on_my_burgee: 'My Burgee',
      unknown_poaching: 'Unknown/Poaching'
    };
    return labels[type] || type;
  };

  return (
    <div className="reports v2">
      <div className="reports-header">
        <h2>📊 Reports &amp; Analytics</h2>
        <p>Mooring status, usage history, poaching incidents, and club burgee activity</p>
      </div>

      {error && (
        <div className="alert alert-error">
          {error}
          <button onClick={() => setError('')} className="close-btn">×</button>
        </div>
      )}

      <div className="report-selector">
        <div className="report-buttons">
          <button
            className={'report-btn' + (reportType === 'mooring-status' ? ' active' : '')}
            onClick={() => setReportType('mooring-status')}
          >
            🌊 Mooring Status
          </button>
          <button
            className={'report-btn' + (reportType === 'mooring-days' ? ' active' : '')}
            onClick={() => setReportType('mooring-days')}
          >
            📅 Mooring Days & Nights
          </button>
          <button
            className={'report-btn' + (reportType === 'poaching' ? ' active' : '')}
            onClick={() => setReportType('poaching')}
          >
            ⚠️ Poaching
          </button>
          <button
            className={'report-btn' + (reportType === 'club-burgee' ? ' active' : '')}
            onClick={() => setReportType('club-burgee')}
          >
            🚩 Club Burgee
          </button>
          <button
            className={'report-btn' + (reportType === 'usage-history' ? ' active' : '')}
            onClick={() => setReportType('usage-history')}
          >
            📋 Usage History
          </button>
          <button
            className={'report-btn' + (reportType === 'audit-log' ? ' active' : '')}
            onClick={() => setReportType('audit-log')}
          >
            🔍 Audit Log
          </button>
        </div>

        <div className="report-actions">
          {canExport && (
            <button className="export-btn" onClick={downloadCSV}>
              📥 Export CSV
            </button>
          )}
          {reportData && reportData.length > 0 && (
            <button className="print-btn" onClick={printReport}>
              🖨️ Print
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="loading">⏳ Generating report...</div>
      ) : reportData ? (
        <div className="report-content">

          {reportType === 'mooring-status' && (
            <div className="usage-history-report">
              <h3>🌊 Current Mooring Status</h3>
              {reportData.length === 0 ? (
                <p>No boats currently on moorings</p>
              ) : (
                Object.entries(
                  reportData.reduce((harbors, mooring) => {
                    if (!harbors[mooring.harbor_name]) harbors[mooring.harbor_name] = [];
                    harbors[mooring.harbor_name].push(mooring);
                    return harbors;
                  }, {})
                ).map(([harbor, moorings]) => (
                  <div key={harbor} className="harbor-section">
                    <h4>{harbor}</h4>
                    <table className="history-table">
                      <thead>
                        <tr>
                          <th>Mooring</th>
                          <th>Member</th>
                          <th>Boat</th>
                          <th>Type</th>
                          <th>Registration</th>
                          <th>Departure</th>
                        </tr>
                      </thead>
                      <tbody>
                        {moorings.map(m => (
                          <tr key={m.mooring_id} className={'history-row ' + m.boat_type}>
                            <td>{m.mooring_number}</td>
                            <td>{m.member_name || '—'}</td>
                            <td>{m.boat_name}</td>
                            <td><span className="type-pill">{boatTypeLabel(m.boat_type)}</span></td>
                            <td>{m.boat_registration || '—'}</td>
                            <td>{m.departure_date}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))
              )}
            </div>
          )}

          {reportType === 'usage-history' && (
            <div className="usage-history-report">
              <h3>📋 Mooring Usage History</h3>
              {reportData.length === 0 ? (
                <p>No usage history found</p>
              ) : (
                Object.entries(
                  reportData.reduce((acc, row) => {
                    if (!acc[row.harbor_name]) acc[row.harbor_name] = [];
                    acc[row.harbor_name].push(row);
                    return acc;
                  }, {})
                ).map(([harbor, rows]) => (
                  <div key={harbor} className="harbor-section">
                    <h4>{harbor}</h4>
                    <table className="history-table">
                      <thead>
                        <tr>
                          <th>Member</th>
                          <th>Mooring</th>
                          <th>Boat</th>
                          <th>Boat Class</th>
                          <th>Type</th>
                          <th>Check-in</th>
                          <th>Departure</th>
                          <th>Status</th>
                          <th>Days</th>
                          <th>📸</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map(r => (
                          <tr key={r.id} className={'history-row status-' + r.status}>
                            <td>{r.member_name || <em>Unknown</em>}</td>
                            <td>{r.mooring_id}</td>
                            <td>{r.boat_name}</td>
                            <td>{r.member_type_boat || '—'}</td>
                            <td><span className="type-pill">{boatTypeLabel(r.boat_type)}</span></td>
                            <td>{r.check_in_date}</td>
                            <td>{r.departure_date}</td>
                            <td><span className={'status-pill ' + r.status}>{r.status}</span></td>
                            <td>{r.days_used}</td>
                            <td>{r.photo_count > 0 ? '📸 ' + r.photo_count : ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))
              )}
            </div>
          )}

          {reportType === 'mooring-days' && (
            <div className="usage-history-report">
              <h3>📅 Mooring Nights</h3>
              {reportData.length === 0 ? (
                <p>No mooring days data found</p>
              ) : (
                Object.entries(
                  reportData.reduce((acc, row) => {
                    if (!acc[row.harbor_name]) acc[row.harbor_name] = [];
                    acc[row.harbor_name].push(row);
                    return acc;
                  }, {})
                ).map(([harbor, rows]) => (
                  <div key={harbor} className="harbor-section">
                    <h4>{harbor}</h4>
                    <table className="history-table">
                      <thead>
                        <tr>
                          <th>Member</th>
                          <th>Mooring</th>
                          <th>Boat</th>
                          <th>Boat Class</th>
                          <th>Type</th>
                          <th>Check-in</th>
                          <th>Departure</th>
                          <th>Checked Out</th>
                          <th>Status</th>
                          <th>Mooring Nights</th>
                          <th>Mooring Days</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r, idx) => (
                          <tr key={idx} className={'history-row status-' + r.status}>
                            <td>{r.member_name || <em>Unknown</em>}</td>
                            <td>{r.mooring_id}</td>
                            <td>{r.boat_name}</td>
                            <td>{r.member_type_boat || '—'}</td>
                            <td><span className="type-pill">{boatTypeLabel(r.boat_type)}</span></td>
                            <td>{r.check_in_date}</td>
                            <td>{r.departure_date}</td>
                            <td>{r.checked_out_date || '—'}</td>
                            <td><span className={'status-pill ' + r.status}>{r.status}</span></td>
                            <td>{r.mooring_nights != null ? r.mooring_nights : '—'}</td>
                            <td><strong>{r.mooring_days != null ? r.mooring_days : '—'}</strong></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))
              )}
            </div>
          )}

          {reportType === 'poaching' && (
            <div className="usage-history-report">
              <h3>⚠️ Poaching / Unknown Boats</h3>
              {reportData.length === 0 ? (
                <p>✓ No poaching boats detected!</p>
              ) : (
                <table className="history-table">
                  <thead>
                    <tr>
                      <th>Boat Name</th>
                      <th>Registration</th>
                      <th>Hailing Port</th>
                      <th>Mooring</th>
                      <th>Harbor</th>
                      <th>Date Reported</th>
                      <th>📸</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.map((p, idx) => (
                      <tr key={idx} className="history-row">
                        <td>{p.boat_name}</td>
                        <td>{p.boat_registration}</td>
                        <td>{p.hailing_port || '—'}</td>
                        <td>{p.mooring_id}</td>
                        <td>{p.harbor_name}</td>
                        <td>{p.reported_date}</td>
                        <td>{p.photo_count > 0 ? '📸 ' + p.photo_count : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {reportType === 'club-burgee' && (
            <div className="usage-history-report">
              <h3>🚩 Club Burgee Activity</h3>
              {reportData.length === 0 ? (
                <p>No club burgee activity</p>
              ) : (
                <table className="history-table">
                  <thead>
                    <tr>
                      <th>Member</th>
                      <th>Boat Used</th>
                      <th>Own Boat</th>
                      <th>Mooring</th>
                      <th>Harbor</th>
                      <th>Check-in</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.map((c, idx) => (
                      <tr key={idx} className="history-row">
                        <td>{c.member_name}</td>
                        <td>{c.borrowed_boat_name}</td>
                        <td>{c.own_boat_name || '—'}</td>
                        <td>{c.mooring_id}</td>
                        <td>{c.harbor_name}</td>
                        <td>{c.check_in_date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {reportType === 'audit-log' && (
            <div className="usage-history-report">
              <h3>🔍 Audit Log</h3>
              {reportData.length === 0 ? (
                <p>No audit log entries found</p>
              ) : (
                <table className="history-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Action</th>
                      <th>Harbor</th>
                      <th>Mooring</th>
                      <th>Boat</th>
                      <th>Description</th>
                      <th>Changed By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.map((a, idx) => (
                      <tr key={idx} className={'history-row action-' + (a.action || '').toLowerCase()}>
                        <td>{a.created_at ? a.created_at.split('T')[0] : ''}</td>
                        <td><span className={'status-pill ' + (a.action || '').toLowerCase()}>{a.action}</span></td>
                        <td>{a.harbor_name || '—'}</td>
                        <td>{a.mooring_number || '—'}</td>
                        <td>{a.boat_name || '—'}</td>
                        <td>{a.action_description}</td>
                        <td>{a.changed_by_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

        </div>
      ) : null}
    </div>
  );
}

export default Reports;
